import {
  Material,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  Vector3,
} from "three";
import { SceneInfo } from "../api/types/Scene";
import { Engine } from "../engine/Engine";
import { Corner } from "../shape/Corner";
import { Line } from "../shape/Line";
import { Room } from "../shape/Scene";
import { engineRequestAfter, engineRequestBefore } from "./EngineUtils";
import { logger } from "./Logger";
import { SugarModel } from "../loader/SugarModel";

export const filterByMaterialName = (
  mainObject: Object3D,
  regex: RegExp,
  log = false
) => {
  let r: any = [];
  mainObject.traverse((e) => {
    if (!r.includes(e)) {
      if (e.isObject3D) {
        logger.debug(e);
        const m = (e as Mesh).material as
          | MeshStandardMaterial
          | MeshPhysicalMaterial;
        let addElement;
        //if it is  array regex
        if (Array.isArray(regex)) {
          let filterMathces = regex.filter((reg) => {
            return m && m.name && m.name.match(reg);
          });
          if (filterMathces.length > 0) addElement = e;
        } //if it is single regex
        else if (m && m.name && m.name.match(regex)) addElement = e;

        if (addElement) r.push(addElement);
      }
    }
  });
  return r;
};

export const filterByStatement = (
  mainObject: Object3D,
  func: (value: unknown) => boolean
) => {
  let r: any = [];
  mainObject.traverse((e) => {
    if (func && func(e)) r.push(e);
  });
  return r;
};

export const convertMaterialsToPhysical = (object: Object3D) => {
  const r: any = {};
  object.traverse((cobj) => {
    if (cobj instanceof Mesh && !r[cobj.material.id]) {
      const mpm = new MeshStandardMaterial();
      r[cobj.material.id] = copyMaterial(cobj.material, mpm);
    }
    if (cobj instanceof Mesh) {
      cobj.material = r[cobj.material.id];
    }
  });

  return r;
};

const copyMaterial = (material: Material, material2: Material) => {
  return material;
};

export const listMaterialByName = (object: Object3D, name: string) => {
  const r: Material[] = [];
  object.traverse((cobj) => {
    if (
      cobj instanceof Mesh &&
      cobj.material.name === name &&
      !r.includes(cobj.material as Material)
    ) {
      r.push(cobj.material);
    }
  });

  return r;
};

export const listMeshByName = (object: Object3D, name: string) => {
  const r: Mesh[] = [];
  object.traverse((cobj) => {
    if (
      cobj instanceof Mesh &&
      cobj.material.name === name &&
      !r.includes(cobj)
    ) {
      r.push(cobj);
    }
  });

  return r;
};

export function nextWall(of?: Line | Corner): Line | undefined {
  if (!of) return undefined;

  if (of instanceof Corner) {
    const start = of;
    return start?.joints
      ?.filter((j) => j instanceof Line && j.isConstruct)
      .map((w) => w as Line)
      .find((w) => w.start == start);
  } else {
    const end = of.end;
    return end?.joints
      ?.filter((j) => j instanceof Line && j.isConstruct)
      .map((w) => w as Line)
      .find((w) => w.start == end);
  }
}

export function next(
  of?: Line | Corner,
  callback?: (w: Line) => boolean
): Line | undefined {
  if (!of) return undefined;

  if (of instanceof Corner) {
    const start = of;
    return start?.joints
      ?.filter(
        (j) =>
          j instanceof Line &&
          j.isConstruct &&
          ((callback && callback(j as Line)) || !callback)
      )
      .map((w) => w as Line)
      .find((w) => w.start == start);
  } else {
    const end = of.end;
    return end?.joints
      ?.filter(
        (j) =>
          j instanceof Line &&
          j.isConstruct &&
          ((callback && callback(j as Line)) || !callback)
      )
      .map((w) => w as Line)
      .find((w) => w.start == end);
  }
}

export function prevWall(of?: Line | Corner): Line | undefined {
  if (!of) return undefined;

  if (of instanceof Corner) {
    const end = of;
    return end?.joints
      ?.filter((j) => j instanceof Line && j.isConstruct)
      .map((w) => w as Line)
      .find((w) => w.end == end);
  } else {
    const start = of.start;
    const next = start?.joints
      ?.filter((j) => j instanceof Line && j.isConstruct)
      .find((w) => (w as Line).end == start);
    return next as Line;
  }
}

export function prev(
  of?: Line | Corner,
  callback?: (w: Line) => boolean
): Line | undefined {
  if (!of) return undefined;

  if (of instanceof Corner) {
    const end = of;
    return end?.joints
      ?.filter(
        (j) =>
          j instanceof Line &&
          j.isConstruct &&
          ((callback && callback(j as Line)) || !callback)
      )
      .map((w) => w as Line)
      .find((w) => w.end == end);
  } else {
    const start = of.start;
    const next = start?.joints
      ?.filter(
        (j) =>
          j instanceof Line &&
          j.isConstruct &&
          ((callback && callback(j as Line)) || !callback)
      )
      .find((w) => (w as Line).end == start);
    return next as Line;
  }
}

export function pointFallsOnLine(wall: Line, point: Vector3) {
  let v = wall.start.point.clone().sub(wall.end.point);
  const w = point.clone().sub(wall.end.point);

  let next: Line | undefined = wall;
  while (v && v.length() == 0) {
    next = nextWall(next);
    let dir = next!.direction;
    if (dir?.length() != 0) {
      v = nextWall(wall)!.direction!.clone()!;
    } else {
      next = nextWall(next);
    }
  }
  return w.dot(v) > 0;
}

export function findConcavingWall(wall: Line) {
  const visited: Line[] = [wall];
  let w = nextWall(wall);
  while (w && !visited.includes(w)) {
    visited.push(w);
    if (w && pointFallsOnLine(wall, w.end.point)) {
      return w.end;
    }
    w = nextWall(w);
  }
  return undefined;
}

export function nextCorner(corner: Corner): Corner {
  const nextWall = corner.joints
    ?.filter((j) => j instanceof Line && j.isConstruct)
    .find((w) => (w as Line).start == corner) as Line;
  return nextWall.end as Corner;
}

export function getProductMap(
  scene: SceneInfo
): Map<{ name: string; id: number }, number> {
  const productArray = scene.productInstances?.map((inst: any) => {
    const product = scene.products?.find((p: any) => p.id === inst.model);
    return { name: product?.name, id: product?.id };
  });

  const productMap = new Map<{ name: string; id: number }, number>();

  productArray?.forEach((product: any) => {
    if (productMap.has(product)) {
      productMap.set(product, productMap.get(product)! + 1);
    } else {
      productMap.set(product, 1);
    }
  });

  return productMap;
}

export function isPartOfARoom(wall: Line): boolean {
  const visited: Line[] = [wall];

  let w = nextWall(wall);
  while (w && !visited.includes(w)) {
    visited.push(w);
    w = nextWall(w);
  }
  return w == wall;
}

/**
 * This does not work properly on concaves
 */
/* export function fixRoomClockwise(wall: Line): void {
  let d = 0;

  iterateFollowingWalls(wall, (w) => {
    if (wall != w) d += wall.direction!.cross(w.direction!).y;
  });

  if (d > 0) {
    iterateFollowingWalls(wall, (c) => {
      if (!c) return;
      const start = c.start;
      c.start = c.end;
      c.end = start;
    });
  }
} */

export function fixRoomClockwise(wall: Line): void {
  const points: Vector3[] = [];

  iterateFollowingWalls(wall, (w) => points.push(w.start.point));

  points.push(points[0]);

  let area = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    area += p0.x * p1.z - p1.x * p0.z;
  }

  if (area < 0) {
    iterateFollowingWalls(wall, (w) => {
      const temp = w.start;
      w.start = w.end;
      w.end = temp;
      w.direction?.copy(w.direction!.negate());
    });
  }
}

export function getFollowingWalls(wall: Line) {
  const visited: Line[] = [wall];

  let w = nextWall(wall);
  while (w && !visited.includes(w)) {
    visited.push(w);
    w = nextWall(w);
  }
  return visited;
}

export function getWallEnd(wall: Line) {
  let w = wall;
  let n = nextWall(w);
  while (n) {
    w = n;
    n = nextWall(w);
  }
  return w;
}

export function getWallStart(wall: Line) {
  let w = wall;
  let n = prevWall(w);
  while (n) {
    w = n;
    n = prevWall(w);
  }
  return w;
}

export function getWallsBetween(wall1: Line, wall2: Line) {
  const visited: Line[] = [wall1];

  let w = nextWall(wall1);
  while (w && w != wall2 && !visited.includes(w)) {
    visited.push(w);
    w = nextWall(w);
  }
  if (w != wall2) return [];
  return visited.filter((w, i, a) => a.indexOf(w) == i);
}

export function iterateFollowingWalls(
  wall: Line,
  f: (current: Line, next?: Line) => void
) {
  const visited: Line[] = [];

  let w: Line | undefined = wall;
  while (w && !visited.includes(w)) {
    visited.push(w);
    let n = nextWall(w);
    f(w, n);
    w = n;
  }
}

// TODO: This function is not working properly. Refine when placements are resolved
export function repositionModels(models: SugarModel[]) {
  let totalLength = 0;
  models.forEach((m) => {
    totalLength += m.localObb.halfSize.length() * 2;
  });

  let currentLength = -0.5 * totalLength + models[0].localObb.halfSize.length();

  models.forEach((m) => {
    m.position.set(currentLength, 0, 0);
    currentLength += m.localObb.halfSize.length() * 2;
  });
}

export function isEndOfALine(corner: Corner): boolean {
  return (
    corner.joints?.some((j) => j instanceof Line && j.end === corner) || false
  );
}

export function isStartOfALine(corner: Corner): boolean {
  return (
    corner.joints?.some((j) => j instanceof Line && j.start === corner) || false
  );
}

export function reverseAllPreviousWalls(from: Line | Corner) {
  let w = prevWall(from);
  while (w) {
    let p = prevWall(w);
    const start = w.start;
    w.start = w.end;
    w.end = start;
    w = p;
  }
}

export function changeWallLength(wall: Line, len: number) {
  const curLen = wall.length;
  const diff = len - curLen;
  if (diff == 0) return;

  const dir = wall.direction!.clone();
  const shift = dir.multiplyScalar(diff);

  const lastCr = findConcavingWall(wall);
  if (!lastCr) {
    logger.error("No corner to shift!");
    wall.end.point.add(shift);
    wall.end.isDirty = true;
    wall.isDirty = true;
    (wall.parent as Room).updateUnfinishedCorners();
  } else {
    for (
      let c = wall.end;
      lastCr != null && c != lastCr;
      c = nextCorner(c as any)
    ) {
      c.point.add(shift);
      c.isDirty = true;
      c.joints?.forEach((w) => {
        w.isDirty = true;
      });
    }

    wall.joints?.forEach((w) => {
      w.isDirty = true;
    });
    engineRequestBefore(
      () => {
        const scene = wall.parent as Room;
        Engine.activeEngine.activeCamera?.fit(scene?.root!);
      },
      true
    );

    //Engine.activeEngine.dispatchEvent
  }

  engineRequestAfter(
    () => {
      Engine.activeEngine.dispatchCustomEvent("resized-walls");
    },
    true
  );
}

export function reverseAllFollowingWalls(from: Line | Corner) {
  let w = nextWall(from);
  while (w) {
    let n = nextWall(w);
    const start = w.start;
    w.start = w.end;
    w.end = start;
    w = n;
  }
}

export function closestPointOnLine(
  point: Vector3,
  line: Line,
  projection = new Vector3(1, 0, 1)
) {
  const bPoint = point.clone().multiply(projection);
  const lPoint = line.start.point.clone();
  const lDirection = line.direction!.clone();

  const diff = bPoint.clone().sub(lPoint);
  const diffLength = diff.length();
  diff.normalize();
  return lPoint.add(
    lDirection.multiplyScalar(diff.dot(lDirection) * diffLength)
  );
}
